/**
 * QUAZI Application Root
 * 
 * Main application component that handles:
 * - Routing configuration
 * - Authentication state management
 * - Session expiry warnings
 * - Error boundaries
 * - Toast notifications
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import { ExternalPortal } from './features/discovery';
import { SurveyResponder } from './features/survey';

// Components
import { AppLoadingScreen, ErrorBoundary, ToastProvider } from './components';

// Providers
import { ThemeProvider } from './providers/ThemeProvider';

// Hooks
import { useAuth } from './hooks';

// Styles
import './styles/globals.css';

function App() {
  const {
    authenticated,
    loading,
    handleLoginSuccess,
    handleLogout,
  } = useAuth();

  if (loading) return <AppLoadingScreen />;

  return (
    <ThemeProvider>
    <ToastProvider>
      <ErrorBoundary>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route 
              path="/login" 
              element={
                authenticated ? 
                  <Navigate to="/dashboard" replace /> : 
                  <LoginPage onLoginSuccess={handleLoginSuccess} />
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                authenticated ? 
                  <ErrorBoundary fallbackMessage="There was an error loading the dashboard. Please try refreshing.">
                    <DashboardPage onLogout={handleLogout} />
                  </ErrorBoundary> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/discover/:token" 
              element={
                <ErrorBoundary fallbackMessage="There was an error loading the page.">
                  <ExternalPortal />
                </ErrorBoundary>
              } 
            />
            <Route 
              path="/survey/:token" 
              element={
                <ErrorBoundary fallbackMessage="There was an error loading the survey.">
                  <SurveyResponder />
                </ErrorBoundary>
              } 
            />
            <Route 
              path="/survey/p/:token" 
              element={
                <ErrorBoundary fallbackMessage="There was an error loading the survey.">
                  <SurveyResponder />
                </ErrorBoundary>
              } 
            />
            <Route 
              path="*" 
              element={<Navigate to={authenticated ? "/dashboard" : "/login"} replace />} 
            />
          </Routes>
        </Router>
      </ErrorBoundary>
    </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
